// src/components/suppliers/SupplierEditModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Row, Col, Divider, Tag } from 'antd';
import { updateSupplier } from '../../services/supplierService';
import { getAllBrands, getAllCategories } from '../../services/brandsService';

const { Option } = Select;

const SupplierEditModal = ({ visible, onClose, onSupplierUpdated, supplier }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    
    // Brands and Categories state
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(true);

    // Check if supplier is informal
    const isInformalSupplier = supplier?.created_without_cuit === true || 
                               supplier?.documento_nro?.startsWith('INFORMAL-');

    // Fetch brands and categories on component mount
    useEffect(() => {
        const fetchBrandsAndCategories = async () => {
            try {
                setLoadingBrands(true);
                const [brandsData, categoriesData] = await Promise.all([
                    getAllBrands(),
                    getAllCategories()
                ]);
                console.log('Edit Modal - Brands loaded:', brandsData.length, brandsData);
                console.log('Edit Modal - Categories loaded:', categoriesData.length, categoriesData);
                setBrands(brandsData);
                setCategories(categoriesData);
            } catch (error) {
                console.error('Error fetching brands/categories:', error);
                message.warning('No se pudieron cargar las marcas o categorías');
            } finally {
                setLoadingBrands(false);
            }
        };

        if (visible) {
            fetchBrandsAndCategories();
        }
    }, [visible]);

    // Pre-fill form when supplier data changes and brands/categories are loaded
    useEffect(() => {
        if (supplier && visible && !loadingBrands && brands.length > 0) {
            // Ensure unique values and filter out any invalid entries
            const uniqueBrands = supplier.related_brands 
                ? [...new Set(supplier.related_brands)].filter(id => brands.some(b => b.value === id))
                : [];
            const uniqueCategories = supplier.related_categories 
                ? [...new Set(supplier.related_categories)].filter(id => categories.some(c => c.value === id))
                : [];
            
            console.log('Setting form values - Brands:', uniqueBrands, 'Categories:', uniqueCategories);
            
            form.setFieldsValue({
                razon_social: supplier.razon_social,
                condicion_impositiva: supplier.condicion_impositiva,
                direccion: supplier.direccion || '',
                localidad: supplier.localidad || '',
                provincia: supplier.provincia || '',
                codigopostal: supplier.codigopostal || '',
                email: supplier.email || '',
                telefono: supplier.telefono || '',
                estado: supplier.estado || 'ACTIVO',
                related_brands: uniqueBrands,
                related_categories: uniqueCategories
            });
        }
    }, [supplier, visible, form, loadingBrands, brands, categories]);

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            // Prepare update data (only fields that can be updated)
            const updateData = {
                razon_social: values.razon_social,
                // For informal suppliers, keep 'NO ESPECIFICADO', otherwise use form value
                condicion_impositiva: isInformalSupplier ? 'NO ESPECIFICADO' : values.condicion_impositiva,
                direccion: values.direccion || '',
                localidad: values.localidad || '',
                provincia: values.provincia || '',
                codigopostal: values.codigopostal || '',
                email: values.email || '',
                telefono: values.telefono || '',
                estado: values.estado,
                related_brands: values.related_brands || [],
                related_categories: values.related_categories || []
            };

            const response = await updateSupplier(supplier.id, updateData);

            if (response.success) {
                message.success('Proveedor actualizado exitosamente');
                onSupplierUpdated(response.supplier);
                onClose();
            }
        } catch (error) {
            console.error('Error updating supplier:', error);
            
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                message.error(errorData.message || 'Error al actualizar proveedor');
            } else {
                message.error('Error al actualizar proveedor');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle modal close
    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    // Format CUIT with hyphens for display
    const formatCuit = (cuit) => {
        if (!cuit) return '';
        const cleaned = cuit.replace(/\D/g, '');
        if (cleaned.length === 11) {
            return `${cleaned.slice(0, 2)}-${cleaned.slice(2, 10)}-${cleaned.slice(10)}`;
        }
        return cuit;
    };

    return (
        <Modal
            title="Editar Proveedor"
            open={visible}
            onCancel={handleCancel}
            width={750}
            style={{ top: 5 }}
            bodyStyle={{ paddingTop: 6, paddingBottom: 6 }}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancelar
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    loading={loading}
                    onClick={handleSubmit}
                >
                    Actualizar Proveedor
                </Button>
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                size="small"
            >
                <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>
                    {isInformalSupplier ? 'Proveedor Informal' : 'CUIT (No modificable)'}
                </Divider>
                
                <Form.Item label={isInformalSupplier ? "Tipo" : "CUIT"} style={{ marginBottom: 6 }}>
                    {isInformalSupplier ? (
                        <Tag color="orange" style={{ fontSize: 14, padding: '4px 12px' }}>Proveedor Informal (Sin CUIT)</Tag>
                    ) : (
                        <Input
                            value={formatCuit(supplier?.documento_nro)}
                            disabled
                            style={{ backgroundColor: '#f5f5f5' }}
                        />
                    )}
                </Form.Item>

                <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>Información del Proveedor</Divider>

                <Form.Item
                    name="razon_social"
                    label="Razón Social"
                    rules={[{ required: true, message: 'Por favor ingrese la razón social' }]}
                    style={{ marginBottom: 6 }}
                >
                    <Input placeholder="Empresa SA" />
                </Form.Item>

                <Form.Item
                    name="condicion_impositiva"
                    label="Condición Impositiva"
                    rules={[{ required: !isInformalSupplier, message: 'Por favor seleccione la condición impositiva' }]}
                    style={{ marginBottom: 6 }}
                >
                    {isInformalSupplier ? (
                        <Input 
                            value="NO ESPECIFICADO" 
                            disabled 
                            style={{ backgroundColor: '#fff7e6', color: '#d46b08' }}
                        />
                    ) : (
                        <Select placeholder="Seleccione condición impositiva">
                            <Option value="RESPONSABLE INSCRIPTO">Responsable Inscripto</Option>
                            <Option value="MONOTRIBUTO">Monotributo</Option>
                            <Option value="EXENTO">Exento</Option>
                            <Option value="CONSUMIDOR FINAL">Consumidor Final</Option>
                        </Select>
                    )}
                </Form.Item>

                <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>Datos de Contacto</Divider>

                <Form.Item
                    name="direccion"
                    label="Dirección"
                    style={{ marginBottom: 6 }}
                >
                    <Input placeholder="Av. Corrientes 1234" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            name="localidad"
                            label="Localidad"
                            style={{ marginBottom: 6 }}
                        >
                            <Input placeholder="CABA" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            name="provincia"
                            label="Provincia"
                            style={{ marginBottom: 6 }}
                        >
                            <Input placeholder="Buenos Aires" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            name="codigopostal"
                            label="Código Postal"
                            style={{ marginBottom: 6 }}
                        >
                            <Input placeholder="1043" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="telefono"
                            label="Teléfono"
                            style={{ marginBottom: 6 }}
                        >
                            <Input placeholder="011-1234-5678" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="email"
                            label="Email"
                            rules={[{ type: 'email', message: 'Email inválido' }]}
                            style={{ marginBottom: 6 }}
                        >
                            <Input placeholder="email@ejemplo.com" />
                        </Form.Item>
                    </Col>
                </Row>

                <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>Marcas y Categorías Relacionadas</Divider>

                <Form.Item
                    name="related_brands"
                    label="Marcas Relacionadas"
                    style={{ marginBottom: 6 }}
                >
                    <Select
                        mode="multiple"
                        placeholder="Seleccione marcas"
                        loading={loadingBrands}
                        disabled={loadingBrands}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                        }
                        options={brands}
                        tagRender={(props) => {
                            const { label, closable, onClose } = props;
                            const colors = ['blue', 'cyan', 'geekblue', 'purple', 'magenta'];
                            const colorIndex = brands.findIndex(b => b.label === label) % colors.length;
                            return (
                                <Tag
                                    color={colors[colorIndex]}
                                    closable={closable}
                                    onClose={onClose}
                                    style={{ marginRight: 3 }}
                                >
                                    {label}
                                </Tag>
                            );
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="related_categories"
                    label="Categorías Relacionadas"
                    style={{ marginBottom: 6 }}
                >
                    <Select
                        mode="multiple"
                        placeholder="Seleccione categorías"
                        loading={loadingBrands}
                        disabled={loadingBrands}
                        allowClear
                        showSearch
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        options={categories}
                        notFoundContent={loadingBrands ? 'Cargando...' : 'No hay categorías disponibles'}
                        tagRender={(props) => {
                            const { label, closable, onClose } = props;
                            return (
                                <Tag
                                    color="green"
                                    closable={closable}
                                    onClose={onClose}
                                    style={{ marginRight: 3 }}
                                >
                                    {label}
                                </Tag>
                            );
                        }}
                    />
                </Form.Item>

                <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>Estado</Divider>

                <Form.Item
                    name="estado"
                    label="Estado del Proveedor"
                    rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
                    style={{ marginBottom: 2 }}
                >
                    <Select placeholder="Seleccione estado">
                        <Option value="ACTIVO">Activo</Option>
                        <Option value="INACTIVO">Inactivo</Option>
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default SupplierEditModal;
